import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import useSWR from "swr";
import {
  Table,
  Input,
  Button,
  Space,
  Tag,
  Checkbox,
  message,
  Popconfirm,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  getCommunityComments,
  forceDeleteCommunityComment,
} from "../api/domainApi";
import { CmntyAdminCommentListItem } from "../types/domain";
import PageHeader from "../components/common/PageHeader";
import AdminAuthorModal from "../components/community/AdminAuthorModal";
import AdminCommentModal, {
  AdminCommentTarget,
} from "../components/community/AdminCommentModal";
import { AuditLogBadge } from "../components/community/AuditLogTimeline";

const PAGE_SIZE = 20;

const CommunityCommentPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [postId, setPostId] = useState(searchParams.get("postId") ?? "");
  const [postIdInput, setPostIdInput] = useState(
    searchParams.get("postId") ?? "",
  );
  const [keyword, setKeyword] = useState("");
  const [kwInput, setKwInput] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [authorOpen, setAuthorOpen] = useState(false);
  const [commentTarget, setCommentTarget] = useState<AdminCommentTarget | null>(null);

  const swrKey = `/community/comments?p=${page}&post=${postId}&kw=${keyword}&del=${includeDeleted}`;
  const { data, isLoading, mutate } = useSWR(
    swrKey,
    () =>
      getCommunityComments({
        page: page - 1,
        size: PAGE_SIZE,
        postId: postId || undefined,
        keyword,
        includeDeleted,
      }),
    { keepPreviousData: true },
  );

  const act = async (fn: () => Promise<any>, ok: string) => {
    try {
      await fn();
      message.success(ok);
      mutate();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const columns: ColumnsType<CmntyAdminCommentListItem> = [
    { title: "ID", dataIndex: "commentId", width: 70 },
    { title: "게시글ID", dataIndex: "postId", width: 90 },
    {
      title: "구분",
      width: 90,
      render: (_, r) =>
        r.parentCommentId ? (
          <Tag color="geekblue">대댓글</Tag>
        ) : (
          <Tag>댓글</Tag>
        ),
    },
    {
      title: "내용",
      // 이 목록은 최신순 평면 페이징이라 답글이 부모와 다른 페이지로 흩어진다.
      // "대댓글" 태그만으로는 무엇에 대한 답인지 알 수 없어 원댓글 요약을 위에 붙인다.
      render: (_, r) => (
        <div>
          {r.parentContent && (
            <div
              style={{
                fontSize: 12,
                color: "#999",
                marginBottom: 2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              ↳ {r.parentContent}
            </div>
          )}
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {r.content}
          </div>
        </div>
      ),
    },
    {
      title: "작성자",
      dataIndex: "authorUserId",
      width: 180,
      // 관리자 댓글은 user_id 가 null 로 저장된다 — 명의는 별도 매핑에 있다.
      // 관리자가 여럿이면 authorUserId 만으로는 구분이 안 되므로 명의를 같이 보여준다.
      render: (v: number | null, r) =>
        v == null ? (
          <Space size={4}>
            <Tag color="purple" style={{ marginInlineEnd: 0 }}>
              운영자
            </Tag>
            <span>{r.adminAuthorName ?? "-"}</span>
          </Space>
        ) : r.authorNickname ? (
          `${r.authorNickname} (#${v})`
        ) : (
          `#${v}`
        ),
    },
    {
      title: "상태",
      width: 140,
      // 표식은 delYn·contentModifiedAt 으로만 그린다 (목록 조회에 이력 조인 없음).
      // 누르면 그때 이력을 가져온다 — 화면에 표식이 20개여도 요청은 누른 하나뿐이다.
      render: (_, r) => (
        <Space size={4} wrap>
          {r.delYn ? (
            <AuditLogBadge
              targetType="COMMENT"
              targetId={r.commentId}
              label="삭제"
              color="red"
            />
          ) : (
            <Tag color="green">노출</Tag>
          )}
          {!r.delYn && r.contentModifiedAt && (
            <AuditLogBadge
              targetType="COMMENT"
              targetId={r.commentId}
              label="수정됨"
            />
          )}
        </Space>
      ),
    },
    {
      title: "작성일",
      dataIndex: "createdAt",
      width: 130,
      render: (v: string) => (v ? v.replace("T", " ").slice(0, 16) : "-"),
    },
    {
      title: "관리",
      width: 210,
      render: (_, row) => (
        <Space size={4}>
          {/* 답글은 최상위 댓글에만 (답글에 답글 불가) */}
          {!row.parentCommentId && (
            <Button
              size="small"
              disabled={row.delYn}
              onClick={() =>
                setCommentTarget({
                  mode: "reply",
                  parentCommentId: row.commentId,
                  postId: row.postId,
                  // 무엇에 답하는지 모달에서 보이도록 원문을 같이 넘긴다
                  parentContent: row.content,
                  parentAuthor:
                    row.authorUserId == null
                      ? (row.adminAuthorName ?? "운영자")
                      : (row.authorNickname ?? `#${row.authorUserId}`),
                })
              }
            >
              답글
            </Button>
          )}
          {/* 수정은 관리자가 쓴 댓글만 — 서버도 403 으로 막는다 */}
          {row.authorUserId == null && (
            <Button
              size="small"
              disabled={row.delYn}
              onClick={() =>
                setCommentTarget({
                  mode: "edit",
                  commentId: row.commentId,
                  initialContent: row.content,
                })
              }
            >
              수정
            </Button>
          )}
          <Popconfirm
            title="댓글을 강제 삭제하시겠습니까?"
            onConfirm={() =>
              act(() => forceDeleteCommunityComment(row.commentId), "삭제됨")
            }
          >
            <Button size="small" danger disabled={row.delYn}>
              삭제
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="커뮤니티 댓글 관리" />
      <Space style={{ marginBottom: 12 }} wrap>
        <Button onClick={() => setAuthorOpen(true)}>관리자 프로필</Button>
        <Button
          type="primary"
          // 입력만 하고 검색하지 않은 상태면 막는다 — 안 그러면 직전 필터 대상에 써 버린다.
          disabled={!postId || postIdInput !== postId}
          onClick={() =>
            setCommentTarget({ mode: "comment", postId: Number(postId) })
          }
        >
          {postId ? `게시글 #${postId} 에 댓글 쓰기` : "댓글 쓰기 (게시글 ID 검색 후)"}
        </Button>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          본문을 보면서 쓰려면 게시글 관리 &gt; 상세를 이용하세요.
        </Typography.Text>
      </Space>
      <Space style={{ marginBottom: 16 }} wrap>
        <Input
          placeholder="게시글 ID"
          value={postIdInput}
          onChange={(e) => setPostIdInput(e.target.value)}
          onPressEnter={() => {
            setPostId(postIdInput);
            setPage(1);
          }}
          style={{ width: 120 }}
        />
        <Input
          placeholder="내용 검색"
          value={kwInput}
          onChange={(e) => setKwInput(e.target.value)}
          onPressEnter={() => {
            setKeyword(kwInput);
            setPage(1);
          }}
          style={{ width: 220 }}
        />
        <Button
          onClick={() => {
            setPostId(postIdInput);
            setKeyword(kwInput);
            setPage(1);
          }}
        >
          검색
        </Button>
        <Checkbox
          checked={includeDeleted}
          onChange={(e) => {
            setIncludeDeleted(e.target.checked);
            setPage(1);
          }}
        >
          삭제 포함
        </Checkbox>
        <Button
          onClick={() => {
            setPostId("");
            setPostIdInput("");
            setKwInput("");
            setKeyword("");
            setIncludeDeleted(false);
            setPage(1);
          }}
        >
          초기화
        </Button>
      </Space>
      <Table
        rowKey="commentId"
        columns={columns}
        dataSource={data?.content}
        loading={isLoading}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: data?.page?.totalElements,
          onChange: setPage,
          showSizeChanger: false,
        }}
        size="small"
      />

      <AdminAuthorModal open={authorOpen} onClose={() => setAuthorOpen(false)} />
      <AdminCommentModal
        open={commentTarget !== null}
        target={commentTarget}
        onClose={() => setCommentTarget(null)}
        onDone={() => mutate()}
      />
    </div>
  );
};

export default CommunityCommentPage;
